import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Divider, Chip } from '@mui/material';
import { Invoice, Type } from 'models/invoice';
import InvoiceStatusChip from './invoice-status-chip.component';
import PaymentStatusChip from './payment-status-chip.component';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { Helmet } from 'react-helmet-async';
import InvoiceCertify from './invoice-certify.component';

type InvoicePageTitleProps = {
    invoice: Invoice;
    lineItemWithOrderCount: number;
    certifiedOrderCount: number;
    openPriceSpecDialog: () => void;
};
const InvoicePageTitle: FC<InvoicePageTitleProps> = ({
    invoice,
    lineItemWithOrderCount,
    certifiedOrderCount,
    openPriceSpecDialog,
}: InvoicePageTitleProps) => {
    const { t: translate } = useTranslation(['orders', 'invoices']);
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const isCertificationEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CERTIFICATION,
    );

    const invoiceTitle =
        invoice.type === Type.CREDIT ? translate('invoices:CREDIT_INVOICE') : translate('invoices:INVOICE');

    return (
        <Fragment>
            <Box mb={1} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                <Box flex="1 0 auto">
                    {invoice.isCredited ? (
                        <Fragment>
                            <Helmet>
                                <title>{`${translate('INVOICE')}: ${invoice.invoiceNumber || ''}`} | Denthub</title>
                            </Helmet>
                            <Typography variant="h1" sx={{ textDecoration: 'line-through' }}>
                                {translate('INVOICE')}: {invoice.invoiceNumber}
                            </Typography>
                        </Fragment>
                    ) : (
                        <Fragment>
                            <Helmet>
                                <title>{`${invoiceTitle}: ${invoice.invoiceNumber || ''}`} | Denthub</title>
                            </Helmet>
                            <Typography variant="h1">
                                {invoiceTitle}: {invoice.invoiceNumber}
                            </Typography>
                        </Fragment>
                    )}
                </Box>
                {permissions.isLabTechnician(currentUser.type) ? (
                    <Box display="flex" flex="0 0 auto">
                        {invoice.isCredited ? (
                            <Chip label={translate('invoices:CREDITED')} color="error" />
                        ) : (
                            <Fragment>
                                <Box mr={1}>
                                    <PaymentStatusChip status={invoice.paymentStatus || 'UNPAID'} />
                                </Box>
                                <Box>
                                    <InvoiceStatusChip status={invoice.status} />
                                </Box>
                            </Fragment>
                        )}
                    </Box>
                ) : isCertificationEnabled ? (
                    <InvoiceCertify
                        lineItemWithOrderCount={lineItemWithOrderCount}
                        certifiedOrderCount={certifiedOrderCount}
                        invoice={invoice}
                        openPriceSpecDialog={openPriceSpecDialog}
                    />
                ) : undefined}
            </Box>
            <Box mt={1} mb={2}>
                <Divider />
            </Box>
        </Fragment>
    );
};

export default InvoicePageTitle;
